import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_heart-school-kids/artifacts/2bkj1tbs_secrade%20%20logo.jfif';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Programs', path: '/programs' },
    { name: 'Admissions', path: '/admissions' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Facilities', path: '/facilities' },
    { name: 'Contact', path: '/contact' },
    { name: 'Admin', path: '/admin' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-xl bg-white/95 shadow-lg py-2'
          : 'bg-white/90 py-3'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="navbar-logo">
            <img 
              src={LOGO_URL} 
              alt="Sacred Heart School Logo" 
              className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
            />
            <span className="font-heading text-lg sm:text-xl text-slate-800 tracking-wider hidden sm:block">
              SACRED HEART
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 xl:px-4 py-2 rounded-full font-body font-semibold text-sm transition-all duration-300 ${
                  isActive(link.path)
                    ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white'
                    : 'text-slate-600 hover:bg-red-50 hover:text-red-500'
                }`}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <Link
            to="/admissions"
            className="hidden md:block px-5 py-2.5 bg-gradient-to-r from-red-500 to-orange-500 text-white font-body font-bold text-sm rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-red-500/30 active:translate-y-0 active:scale-95"
            data-testid="navbar-cta"
          >
            Enroll Now
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="block lg:hidden p-2 rounded-full hover:bg-red-50 transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            type="button"
          >
            {isOpen ? (
              <X className="w-6 h-6 text-slate-700" />
            ) : (
              <Menu className="w-6 h-6 text-slate-700" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-t border-slate-100"
            data-testid="mobile-menu"
          >
            <div className="max-w-7xl mx-auto px-4 py-4">
              <div className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`px-4 py-3 rounded-xl font-body font-semibold text-base transition-all duration-300 ${
                      isActive(link.path)
                        ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white'
                        : 'text-slate-600 hover:bg-red-50 hover:text-red-500'
                    }`}
                    data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                ))}
                <Link
                  to="/admissions"
                  onClick={() => setIsOpen(false)}
                  className="mt-2 px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white font-body font-bold text-base rounded-xl text-center transition-all duration-300 hover:shadow-lg"
                  data-testid="mobile-navbar-cta"
                >
                  Enroll Now
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
